import { ForwardedRef, forwardRef } from "react";
import {
  Link as ChakraLink,
  LinkProps as ChakraLinkProps,
} from "@chakra-ui/react";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { OptionalKeys } from "utils/typescriptUtilis";

type BaseProps = Omit<ChakraLinkProps, "href">;

type ExternalLinkProps = BaseProps & {
  isExternal: true;
  href?: string;
};

export type InternalLinkProps = BaseProps &
  OptionalKeys<NextLinkProps, "href"> & {
    isExternal?: false;
  };

export type LinkProps = ExternalLinkProps | InternalLinkProps;

function Link(props: LinkProps, ref: ForwardedRef<HTMLAnchorElement>) {
  const { isExternal } = props;

  if (isExternal) {
    const { href } = props;
    return (
      <ChakraLink ref={ref} aria-disabled={!href || undefined} {...props} />
    );
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  const {
    as,
    href,
    locale,
    passHref = true,
    prefetch,
    replace,
    scroll,
    shallow,
    target,
    ...rest
  } = props;
  /* eslint-enable @typescript-eslint/naming-convention */

  if (!href) {
    return <ChakraLink ref={ref} aria-disabled {...rest} />;
  }

  return (
    <NextLink
      {...{
        as,
        href,
        locale,
        passHref,
        prefetch,
        replace,
        scroll,
        shallow,
        target,
      }}
      legacyBehavior
    >
      <ChakraLink ref={ref} {...rest} />
    </NextLink>
  );
}

export default forwardRef(Link);
